import { useMemo, useState } from "react";
import { useMastersStore } from "../data/mastersStore";
import type { Client } from "../data/schemaTypes";
import { Badge, Button, Card, Input, Select } from "../components/ui";
import { ClientDetailDrawer } from "../features/clients/ClientDetailDrawer";
import { ClientEditModal } from "../features/clients/ClientEditModal";
import { CsvUploader } from "../features/imports/CsvUploader";

// Clientes page — listado con filtro por distrito / tipo de negocio /
// búsqueda. Click en una fila abre el drawer con direcciones. El botón
// Editar abre el modal de edición.
export function ClientsPage() {
  const clients = useMastersStore((s) => s.clients);
  const addresses = useMastersStore((s) => s.clientAddresses);

  const [search, setSearch] = useState("");
  const [district, setDistrict] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [detail, setDetail] = useState<Client | null>(null);
  const [editing, setEditing] = useState<Client | null>(null);
  const [uploadClientsOpen, setUploadClientsOpen] = useState(false);
  const [uploadPrefsOpen, setUploadPrefsOpen] = useState(false);
  const hydrate = useMastersStore((s) => s.hydrate);

  const districts = useMemo(
    () => Array.from(new Set(clients.map((c) => c.district).filter(Boolean))) as string[],
    [clients],
  );
  const businessTypes = useMemo(
    () =>
      Array.from(
        new Set(clients.map((c) => c.business_type).filter(Boolean)),
      ) as string[],
    [clients],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (district && c.district !== district) return false;
      if (businessType && c.business_type !== businessType) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        (c.tax_id ?? "").includes(q) ||
        (c.contact_name ?? "").toLowerCase().includes(q)
      );
    });
  }, [clients, search, district, businessType]);

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-ink">Clientes</h1>
          <p className="text-xs text-sub mt-0.5">
            {rows.length} de {clients.length} clientes ·{" "}
            {clients.filter((c) => c.active).length} activos
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setUploadClientsOpen(true)}>
            ⬆ Cargar clientes
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setUploadPrefsOpen(true)}>
            ⬆ Cargar preferencias
          </Button>
          <Button variant="primary" size="sm" disabled>
            + Nuevo cliente
          </Button>
        </div>
      </header>

      <Card padding="sm" className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <Input
            label="Buscar"
            placeholder="Nombre, código, RUC/DNI, contacto…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-44">
          <Select
            label="Distrito"
            value={district}
            onChange={(e) => setDistrict(e.target.value)}
          >
            <option value="">Todos</option>
            {districts.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-44">
          <Select
            label="Tipo de negocio"
            value={businessType}
            onChange={(e) => setBusinessType(e.target.value)}
          >
            <option value="">Todos</option>
            {businessTypes.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </Select>
        </div>
      </Card>

      <div className="bg-card border border-border rounded-lg shadow-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-page text-sub border-b border-border">
            <tr className="text-left">
              <Th>Código</Th>
              <Th>Cliente</Th>
              <Th>RUC / DNI</Th>
              <Th>Contacto</Th>
              <Th>Distrito</Th>
              <Th>Direcciones</Th>
              <Th>Tipo</Th>
              <Th>Estado</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const count = addresses.filter((a) => a.client_id === c.id).length;
              return (
                <tr
                  key={c.id}
                  onClick={() => setDetail(c)}
                  className="border-b border-border last:border-b-0 hover:bg-page/60 cursor-pointer"
                >
                  <td className="px-3 py-2 font-mono text-xs text-primary">
                    {c.code}
                  </td>
                  <td className="px-3 py-2 text-ink font-medium">{c.name}</td>
                  <td className="px-3 py-2 text-sub font-mono text-xs">
                    {c.tax_id_type ? `${c.tax_id_type} ${c.tax_id ?? ""}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-sub">
                    <div>{c.contact_name ?? "—"}</div>
                    {c.contact_phone && (
                      <div className="text-[11px] font-mono">{c.contact_phone}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sub">{c.district ?? "—"}</td>
                  <td className="px-3 py-2 text-ink">{count}</td>
                  <td className="px-3 py-2 text-sub capitalize">
                    {c.business_type ?? "—"}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={c.active ? "delivered" : "pending"}>
                      {c.active ? "Activo" : "Inactivo"}
                    </Badge>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-sub">
                  No hay clientes que coincidan con los filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ClientDetailDrawer
        client={detail}
        onClose={() => setDetail(null)}
        onEdit={() => {
          if (detail) {
            setEditing(detail);
            setDetail(null);
          }
        }}
      />
      <ClientEditModal client={editing} onClose={() => setEditing(null)} />

      <CsvUploader
        open={uploadClientsOpen}
        onClose={() => setUploadClientsOpen(false)}
        title="Cargar clientes desde CSV"
        endpoint="/api/clients/bulk"
        description={
          <>
            Columnas: <code>code</code> y <code>name</code> obligatorias.{" "}
            Opcionales: <code>address</code>, <code>district</code>, <code>city</code>,{" "}
            <code>lat</code>, <code>lon</code>, <code>contact_name</code>,{" "}
            <code>contact_phone</code>, <code>business_type</code>. Upsert por code.
          </>
        }
        columns={[
          { key: "code", aliases: ["code", "codigo", "código", "cod_cliente", "cod. cliente"], required: true },
          { key: "name", aliases: ["name", "nombre", "cliente", "razon social", "razón social"], required: true },
          { key: "address", aliases: ["address", "direccion", "dirección"] },
          { key: "district", aliases: ["district", "distrito"] },
          { key: "city", aliases: ["city", "ciudad"] },
          { key: "lat", aliases: ["lat", "latitud", "ea.latitud"], type: "numberOptional" },
          { key: "lon", aliases: ["lon", "lng", "longitud", "ea.longitud"], type: "numberOptional" },
          { key: "contact_name", aliases: ["contact_name", "contacto", "nombre contacto"] },
          { key: "contact_phone", aliases: ["contact_phone", "telefono", "teléfono"] },
          { key: "business_type", aliases: ["business_type", "tipo", "tipo de negocio"] },
        ]}
        onSuccess={() => hydrate()}
      />

      <CsvUploader
        open={uploadPrefsOpen}
        onClose={() => setUploadPrefsOpen(false)}
        title="Cargar preferencias cliente → chofer"
        endpoint="/api/clients/preferences/bulk"
        description={
          <>
            Columnas: <code>client_code</code> y <code>driver_code</code> obligatorias.{" "}
            Vincula cada cliente con su transportista preferido. Si alguno no existe,
            la fila aparece como error.
          </>
        }
        columns={[
          { key: "client_code", aliases: ["client_code", "cliente", "cod_cliente", "cod. cliente"], required: true },
          { key: "driver_code", aliases: ["driver_code", "chofer", "transportista", "code"], required: true },
        ]}
        onSuccess={() => hydrate()}
      />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wide">
      {children}
    </th>
  );
}
